import { useMemo, useState } from "react";
import { Check, Code2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SOCIALS } from "@/data/content";
import { buildSignatureHtml } from "@/lib/signature";

const social = (id) => SOCIALS.find((s) => s.id === id)?.href || "";

const DEFAULTS = {
  name: "Kelly Robinson",
  title: "Founder & CEO",
  company: "PKR Recruiting",
  phone: "",
  email: "kelly@pkrhires.com",
  website: "pkrhires.com",
  linkedin: "https://www.linkedin.com/in/pkrhires",
  instagram: social("instagram"),
  facebook: social("facebook"),
};

const FIELDS = [
  ["name", "Full name"], ["title", "Title"], ["company", "Company"], ["phone", "Phone"], ["email", "Email"],
  ["website", "Website"], ["linkedin", "LinkedIn URL"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"],
];

const copyRich = async (html) => {
  const item = new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([html], { type: "text/plain" }) });
  await navigator.clipboard.write([item]);
};

export default function Signature() {
  const [form, setForm] = useState(DEFAULTS);
  const [copied, setCopied] = useState("");
  const html = useMemo(() => buildSignatureHtml(form), [form]);

  const copy = async (kind) => {
    try {
      if (kind === "rich") await copyRich(html);
      else await navigator.clipboard.writeText(html);
      setCopied(kind);
      toast.success(kind === "rich" ? "Signature copied — paste into your email settings." : "HTML code copied.");
      setTimeout(() => setCopied(""), 2000);
    } catch {
      toast.error("Copy failed — select the code below and copy manually.");
    }
  };

  return (
    <main data-testid="signature-page" className="min-h-screen bg-snow px-5 py-12 text-ink sm:px-8">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[22rem_1fr]">
        <aside className="border border-navy/10 bg-white p-6">
          <p className="eyebrow">Email signature</p>
          <h1 className="font-heading text-2xl font-bold">Signature details</h1>
          <p className="mt-2 text-sm text-ink/60">Edit the fields — the preview updates live. Leave a field blank to hide it.</p>
          <div className="mt-6 space-y-4">
            {FIELDS.map(([key, label]) => (
              <label key={key} className="block text-xs font-bold uppercase tracking-[0.18em] text-navy">
                {label}
                <input
                  data-testid={`signature-field-${key}`}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className="input-warm mt-1.5 h-11 w-full border px-3 text-sm font-normal normal-case tracking-normal text-ink outline-none"
                />
              </label>
            ))}
          </div>
        </aside>

        <section className="space-y-6">
          <div className="border border-navy/10 bg-white p-8">
            <p className="eyebrow">Preview</p>
            <div data-testid="signature-preview" className="mt-4 overflow-x-auto" dangerouslySetInnerHTML={{ __html: html }} />
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="signature-copy-rich" onClick={() => copy("rich")} className="btn-primary h-12 px-5">
                {copied === "rich" ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />} Copy signature
              </Button>
              <Button data-testid="signature-copy-html" onClick={() => copy("html")} variant="outline" className="h-12 border-navy bg-white px-5 text-navy hover:bg-snow">
                {copied === "html" ? <Check className="mr-2 h-4 w-4" /> : <Code2 className="mr-2 h-4 w-4" />} Copy HTML code
              </Button>
            </div>
            <p className="mt-4 text-xs text-ink/55">
              Gmail / Outlook: use <strong>Copy signature</strong>, then paste into Settings → Signature. Tools that accept raw HTML: use <strong>Copy HTML code</strong>.
            </p>
          </div>
          <div className="border border-navy/10 bg-white p-6">
            <p className="eyebrow">HTML code</p>
            <textarea data-testid="signature-html-code" readOnly value={html} rows={12} className="mt-3 w-full border border-navy/15 bg-snow p-3 font-mono text-xs text-ink outline-none" />
          </div>
        </section>
      </div>
    </main>
  );
}
